/**
 * Store del carrito (cart) actual.
 *
 * Maneja el flujo de captura de servicios: cliente, vehículo, lista de
 * servicios con precio, subtotal y operación pendiente de confirmar.
 *
 * Persiste en sessionStorage durante estado 'draft' para sobrevivir
 * recargas accidentales sin contaminar otras pestañas.
 *
 * NO persiste en localStorage porque el cart debe morir al cerrar la
 * pestaña (los datos en D1 son la fuente de verdad).
 */

import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { Tamano } from '@carwash-pos/shared';

const STORAGE_KEY = 'cwpos_cart';

export interface ServicioCart {
  /** ID temporal del front; null si no se ha enviado al backend. */
  id: number | null;
  /** ID del backend (PK) después de crear el servicio. */
  backendId: number | null;
  /** Paquete del catálogo. */
  paqueteId: number;
  paqueteNombre: string;
  /** Si es precio fijo, true. */
  esPrecioFijo: boolean;
  /** Talla del vehículo (null si es precio fijo o Custom). */
  tamano: Tamano | null;
  /** Precio base (antes de descuentos). */
  precioBase: number;
  /** Precio final (después de promo y descuentos manuales). */
  precioFinal: number;
  /** Descuento aplicado (% o monto). */
  descuentoPct: number;
  descuentoMonto: number;
  /** Si el manager ajustó manualmente el precio base. */
  precioPersonalizado: boolean;
  /** Si es Custom, nombre y precio capturado. */
  customNombre: string | null;
  customPrecio: number | null;
  /** Estado del servicio (en cart siempre es 'pendiente'). */
  estado: 'pendiente' | 'en_proceso' | 'completado' | 'cancelado';
  /** FK al lavador (null hasta que se asigna). */
  lavadorId: number | null;
  lavadorNombre: string | null;
}

export interface CartState {
  clienteId: number | null;
  clienteNombre: string | null;
  clienteApellido: string | null;
  clienteCelular: string | null;
  vehiculoId: number | null;
  vehiculoMarca: string | null;
  vehiculoModelo: string | null;
  vehiculoPlacas: string | null;
  sucursalId: number | null;
  servicios: ServicioCart[];
  notas: string;
}

const estadoInicial: CartState = {
  clienteId: null,
  clienteNombre: null,
  clienteApellido: null,
  clienteCelular: null,
  vehiculoId: null,
  vehiculoMarca: null,
  vehiculoModelo: null,
  vehiculoPlacas: null,
  sucursalId: null,
  servicios: [],
  notas: '',
};

function loadFromStorage(): CartState {
  if (typeof sessionStorage === 'undefined') return { ...estadoInicial };
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...estadoInicial };
    return { ...estadoInicial, ...JSON.parse(raw) } as CartState;
  } catch {
    return { ...estadoInicial };
  }
}

export const useCartStore = defineStore('cart', () => {
  const initial = loadFromStorage();
  const clienteId = ref<number | null>(initial.clienteId);
  const clienteNombre = ref<string | null>(initial.clienteNombre);
  const clienteApellido = ref<string | null>(initial.clienteApellido);
  const clienteCelular = ref<string | null>(initial.clienteCelular);
  const vehiculoId = ref<number | null>(initial.vehiculoId);
  const vehiculoMarca = ref<string | null>(initial.vehiculoMarca);
  const vehiculoModelo = ref<string | null>(initial.vehiculoModelo);
  const vehiculoPlacas = ref<string | null>(initial.vehiculoPlacas);
  const sucursalId = ref<number | null>(initial.sucursalId);
  // Solo protege el cambio de sucursal mientras la captura sigue abierta.
  // No se persiste porque la orden se reanuda desde Historial tras una recarga.
  const capturandoOrden = ref(false);
  const servicios = ref<ServicioCart[]>(initial.servicios);
  const notas = ref<string>(initial.notas);

  // Persistencia automática
  watch(
    [
      clienteId,
      clienteNombre,
      clienteApellido,
      clienteCelular,
      vehiculoId,
      vehiculoMarca,
      vehiculoModelo,
      vehiculoPlacas,
      sucursalId,
      servicios,
      notas,
    ],
    () => {
      if (typeof sessionStorage === 'undefined') return;
      const snapshot: CartState = {
        clienteId: clienteId.value,
        clienteNombre: clienteNombre.value,
        clienteApellido: clienteApellido.value,
        clienteCelular: clienteCelular.value,
        vehiculoId: vehiculoId.value,
        vehiculoMarca: vehiculoMarca.value,
        vehiculoModelo: vehiculoModelo.value,
        vehiculoPlacas: vehiculoPlacas.value,
        sucursalId: sucursalId.value,
        servicios: servicios.value,
        notas: notas.value,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    },
    { deep: true },
  );

  // Getters
  const subtotal = computed(() => servicios.value.reduce((acc, s) => acc + s.precioFinal, 0));
  const total = computed(() => subtotal.value); // sin descuento global
  const cantidadServicios = computed(() => servicios.value.length);
  const estaVacio = computed(() => servicios.value.length === 0);
  /** El vehículo actual es el sentinel "Sin vehículo" (id=1). */
  const vehiculoEsSinVehiculo = computed(() => vehiculoId.value === 1);

  // Acciones
  function setCliente(c: { id: number; nombre: string; apellido: string; celular: string }) {
    clienteId.value = c.id;
    clienteNombre.value = c.nombre;
    clienteApellido.value = c.apellido;
    clienteCelular.value = c.celular;
  }

  function setVehiculo(v: {
    id: number;
    marca?: string;
    modelo: string;
    placas: string | null;
  }) {
    vehiculoId.value = v.id;
    vehiculoMarca.value = v.marca ?? '';
    vehiculoModelo.value = v.modelo;
    vehiculoPlacas.value = v.placas;
  }

  function setSucursal(id: number) {
    sucursalId.value = id;
  }

  function setCapturandoOrden(activa: boolean) {
    capturandoOrden.value = activa;
  }

  function setNotas(n: string) {
    notas.value = n;
  }

  function agregarServicio(s: ServicioCart) {
    // Genera un id temporal negativo para identificarlo en UI
    s.id = -servicios.value.length - 1;
    servicios.value.push(s);
  }

  function removerServicio(tempId: number | null) {
    if (tempId === null) return;
    servicios.value = servicios.value.filter((s) => s.id !== tempId);
  }

  function actualizarServicio(tempId: number | null, patch: Partial<ServicioCart>) {
    if (tempId === null) return;
    const idx = servicios.value.findIndex((s) => s.id === tempId);
    if (idx === -1) return;
    servicios.value[idx] = { ...servicios.value[idx], ...patch };
  }

  function resetear() {
    clienteId.value = null;
    clienteNombre.value = null;
    clienteApellido.value = null;
    clienteCelular.value = null;
    vehiculoId.value = null;
    vehiculoMarca.value = null;
    vehiculoModelo.value = null;
    vehiculoPlacas.value = null;
    sucursalId.value = null;
    capturandoOrden.value = false;
    servicios.value = [];
    notas.value = '';
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  return {
    // Estado
    clienteId,
    clienteNombre,
    clienteApellido,
    clienteCelular,
    vehiculoId,
    vehiculoMarca,
    vehiculoModelo,
    vehiculoPlacas,
    sucursalId,
    capturandoOrden,
    servicios,
    notas,
    // Getters
    subtotal,
    total,
    cantidadServicios,
    estaVacio,
    vehiculoEsSinVehiculo,
    // Acciones
    setCliente,
    setVehiculo,
    setSucursal,
    setCapturandoOrden,
    setNotas,
    agregarServicio,
    removerServicio,
    actualizarServicio,
    resetear,
  };
});
